/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Col, Form, Row, Button, Modal } from 'react-bootstrap';
import { SalesInvoice } from '../SalesInvoiceForm';
import CreatableSelect from 'react-select/creatable';
import { useAppContext } from 'providers/AppProvider';
import axiosInstance from 'utils/axiosInterceptors';
import useDebounce from 'hooks/useDebounce';
import DatePicker from 'components/base/DatePicker';
import useDateState from 'hooks/useDateState';
import { UilCalendarAlt } from '@iconscout/react-unicons';
import { TPaymentMethod, TUser } from 'utils/types';
import { AxiosError } from 'axios';
import { useBranchContext } from 'providers/BranchProvider';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';

export const darkModeStyles = {
  control: (provided: any) => ({
    ...provided,
    backgroundColor: '#141824',
    borderColor: '#4B5563',
    color: '#FFFFFF',
    height: '2.3rem',
    marginTop: 23
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: '#9f9fa4'
  }),
  input: (provided: any) => ({
    ...provided,
    color: '#9f9fa4'
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: '#374151'
  }),
  option: (provided: any, state: { isFocused: any }) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#4B5563' : '#141824',
    color: '#FFFFFF',
    fontSize: '.8rem'
  })
};

export const lightModeStyles = {
  control: (provided: any) => ({
    ...provided,
    backgroundColor: '#FFFFFF',
    borderColor: '#CBD5E0',
    color: '#000000',
    height: '2.3rem',
    marginTop: 23
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: '#000000'
  }),
  input: (provided: any) => ({
    ...provided,
    color: '#000000'
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: '#FFFFFF'
  }),
  option: (provided: any, state: { isFocused: any }) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#EDF2F7' : '#FFFFFF',
    color: '#000000'
  })
};

interface SalesInvoiceInputsProps {
  salesInvoice: SalesInvoice;
  setSalesInvoice: React.Dispatch<React.SetStateAction<SalesInvoice>>;
  salesStaff: TUser[] | null;
  paymentMethods: TPaymentMethod[] | null;
  uniqueCode: string;
  isSalesInvoiceCreated: boolean;
  saveEditedInvoice: () => Promise<void>;
  salesInvoiceId: number;
  getSalesInvoiceItems: () => Promise<void>;
}

const CreateSalesInvoiceInputs = ({
  salesInvoice,
  setSalesInvoice,
  salesStaff,
  paymentMethods,
  uniqueCode,
  isSalesInvoiceCreated,
  saveEditedInvoice,
  salesInvoiceId,
  getSalesInvoiceItems
}: SalesInvoiceInputsProps) => {
  const {
    config: { theme }
  } = useAppContext();
  const { selectedBranch } = useBranchContext();
  const [customerIdFromMembershipNumber, setCustomerIdFromMembershipNumber] =
    useState<number | null>(null);
  const [mergedCustomers, setMergedCustomers] = useState<any[]>([]);
  const { date: invoicedOn, setDate: setInvoicedOn } = useDateState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [memberShipNumberInput, setMemberShipNumberInput] = useState('');
  const debouncedMemberShipNumberInput = useDebounce(
    memberShipNumberInput,
    500
  );
  const [input, setInput] = useState('');
  const debouncedInput = useDebounce(input, 500);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getCustomers = async (search: string = '') => {
    setIsLoading(true);
    try {
      const [individualRes, companyRes] = await Promise.all([
        axiosInstance.get(
          `/api/v1/customers/individuals?page=1&pageSize=10&search=${search}`
        ),
        axiosInstance.get(
          `/api/v1/customers/companies?page=1&pageSize=10&search=${search}`
        )
      ]);

      const mergedFormattedCustomers = [
        { value: 'Cash', label: 'Cash' },

        ...(Array.isArray(individualRes.data.data)
          ? individualRes.data.data.map((customer: any) => ({
              value: customer.customerId?.toString() || '',
              label:
                `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
            }))
          : []),

        ...(Array.isArray(companyRes.data.data)
          ? companyRes.data.data.map((company: any) => ({
              value: company.customerId?.toString() || '',
              label: company.companyName || '',
              paymentTerms: company.paymentTerms || 0
            }))
          : [])
      ];

      setMergedCustomers(mergedFormattedCustomers);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCustomerFromMembershipNumber = async (input: string) => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/customers/membershipNumberLookup/?membershipNumber=${input}`
      );

      const customerId = res.data.customerId;
      setCustomerIdFromMembershipNumber(customerId);

      const existingCustomer = mergedCustomers.find(
        customer => customer.value === customerId.toString()
      );

      // if customer doesnt exist on the partial list, check for the customer from BE if it exist
      if (!existingCustomer) {
        const [companyRes, individualRes] = await Promise.allSettled([
          axiosInstance.get(`/api/v1/customers/companies/${customerId}`),
          axiosInstance.get(`/api/v1/customers/individuals/${customerId}`)
        ]);

        let customerData = null;

        if (companyRes.status === 'fulfilled') {
          customerData = companyRes.value.data;
        } else if (individualRes.status === 'fulfilled') {
          customerData = individualRes.value.data;
        }

        if (customerData) {
          const newCustomer = {
            value: customerData.customerId.toString(),
            label: customerData.firstName
              ? `${customerData.firstName} ${customerData.lastName}`.trim()
              : customerData.companyName || ''
          };

          setMergedCustomers(prevCustomers => [...prevCustomers, newCustomer]);

          handleSalesFormChange('soldTo', newCustomer.value);
          handleSalesFormChange('customerId', newCustomer.value);
          handleSalesFormChange('customerName', newCustomer.label);
        }
      }
    } catch (error) {
      console.error('Error fetching customer:', error);
    }
  };

  const handleSalesFormChange = <K extends keyof SalesInvoice>(
    field: K,
    value: SalesInvoice[K]
  ) => {
    setSalesInvoice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (dates: Date[] | Date | null) => {
    if (dates instanceof Date) {
      setInvoicedOn(dates);
      handleSalesFormChange('invoicedOn', dates.toISOString());
    } else if (Array.isArray(dates) && dates.length > 0) {
      setInvoicedOn(dates[0]);
      handleSalesFormChange('invoicedOn', dates[0].toISOString());
    } else {
      setInvoicedOn(null);
      handleSalesFormChange('invoicedOn', null);
    }
  };

  useEffect(() => {
    getCustomers(debouncedInput);
  }, [debouncedInput]);

  useEffect(() => {
    if (debouncedMemberShipNumberInput) {
      getCustomerFromMembershipNumber(debouncedMemberShipNumberInput);
    }
  }, [debouncedMemberShipNumberInput]);

  useEffect(() => {
    if (salesInvoice.soldTo || salesInvoice.customerName) {
      // Clear discountCode when customer changes
      handleSalesFormChange('discountCode', '');
      saveEditedInvoice();
    }
  }, [salesInvoice.soldTo, salesInvoice.customerName]);

  useEffect(() => {
    if (!customerIdFromMembershipNumber) return;

    const selectedCustomer = mergedCustomers.find(
      customer => customer.value === customerIdFromMembershipNumber.toString()
    );

    if (selectedCustomer) {
      if (selectedCustomer?.paymentTerms > 0) {
        handleSalesFormChange('paymentMethodId', 7);
        handleSalesFormChange('soldTo', '');
        handleSalesFormChange('customerId', selectedCustomer.value);
        handleSalesFormChange('customerName', selectedCustomer.label);
        // Clear discountCode when customer changes via membership number
        handleSalesFormChange('discountCode', '');
        saveEditedInvoice();
      } else {
        handleSalesFormChange('paymentMethodId', 1);
        handleSalesFormChange('soldTo', '');
        handleSalesFormChange('customerId', selectedCustomer.value);
        handleSalesFormChange('customerName', selectedCustomer.label);
        // Clear discountCode when customer changes via membership number
        handleSalesFormChange('discountCode', '');
        saveEditedInvoice();
      }
    }
  }, [customerIdFromMembershipNumber, mergedCustomers]);

  const handleSubmitDiscountCode = async () => {
    if (!isSalesInvoiceCreated || !selectedBranch?.branchDomain) return;
    
    setIsSubmitting(true);
    try {
      const res = await axiosInstance.put(
        `/api/v1/${selectedBranch.branchDomain}/salesInvoices/${salesInvoiceId}/discountCode`,
        { discountCode: discountCodeInput }
      );
      
      // Update the discount code with the value from the response
      setSalesInvoice(prev => ({
        ...prev,
        discountCode: res.data.discountCode || ''
      }));
      
      toast.success('Discount code updated', {
        ...toastConfig,
        autoClose: 2000
      });
      setShowDiscountModal(false);

      // Use the consolidated getSalesInvoiceItems function
      await getSalesInvoiceItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage || 'Failed to update discount code', {
          ...toastConfig,
          autoClose: 3000
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Row className="mb-3">
        <Form.Group as={Col} controlId="invoiceNumber">
          <Form.Label>Invoice Number</Form.Label>
          <Form.Control
            type="text"
            value={salesInvoice.invoiceNumber || ''}
            onChange={e =>
              handleSalesFormChange('invoiceNumber', e.target.value)
            }
            onBlur={saveEditedInvoice}
          />
        </Form.Group>
        <Form.Group as={Col} controlId="soldTo">
          <CreatableSelect
            styles={theme === 'dark' ? darkModeStyles : lightModeStyles}
            value={
              mergedCustomers.find(
                option => option.value === salesInvoice.soldTo
              ) || {
                value: salesInvoice.soldTo,
                label: salesInvoice.soldTo || salesInvoice.customerName
              }
            }
            options={mergedCustomers}
            isLoading={isLoading}
            onChange={newValue => {
              const selectedCustomer = mergedCustomers.find(
                customer => customer.value === newValue.value
              );

              if (newValue.value === 'Cash') {
                handleSalesFormChange('soldTo', 'Cash');
                handleSalesFormChange('customerId', null);
                handleSalesFormChange('customerName', '');
              } else if (newValue.__isNew__ === true) {
                handleSalesFormChange('soldTo', newValue.value);
                handleSalesFormChange('customerName', newValue.label);
                handleSalesFormChange('customerId', null);
                setCustomerIdFromMembershipNumber(null);
              } else if (newValue.value !== newValue.label) {
                handleSalesFormChange('soldTo', '');
                handleSalesFormChange('customerId', selectedCustomer?.value);
                handleSalesFormChange('customerName', selectedCustomer?.label);
              }

              if (selectedCustomer?.paymentTerms > 0) {
                handleSalesFormChange('paymentMethodId', 7);
              } else {
                handleSalesFormChange('paymentMethodId', 1);
              }
            }}
            closeMenuOnSelect
            isSearchable
            onInputChange={newValue => setInput(newValue)}
          />
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col} controlId="invoicedOn">
          <Form.Label>Invoiced On</Form.Label>
          <DatePicker
            placeholder="Invoiced On: Select Date"
            value={invoicedOn ? invoicedOn.toISOString() : undefined}
            onChange={dates => handleDateChange(dates)}
            disabled={isSalesInvoiceCreated}
            options={{
              dateFormat: 'M j, Y',
              locale: {
                firstDayOfWeek: 0
              }
            }}
            icon={
              <UilCalendarAlt
                className="flatpickr-icon text-body-tertiary"
                size={16}
              />
            }
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Select Sales Staff</Form.Label>
          <Form.Select
            value={salesInvoice.salesStaffId || ''}
            onChange={e =>
              handleSalesFormChange('salesStaffId', +e.target.value)
            }
            onBlur={saveEditedInvoice}
          >
            <option value="">Select Staff</option>
            {salesStaff &&
              salesStaff.map(staff => (
                <option key={staff.userId} value={staff.userId}>
                  {staff.firstname} {staff.lastname}
                </option>
              ))}
          </Form.Select>
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col} controlId="paymentReference">
          <Form.Label>Payment Reference</Form.Label>
          <Form.Control
            type="text"
            className="mb-3"
            value={salesInvoice.paymentReference || ''}
            onChange={e =>
              handleSalesFormChange('paymentReference', e.target.value)
            }
            onBlur={saveEditedInvoice}
          />
        </Form.Group>
        <Form.Group as={Col} controlId="remarks">
          <Form.Label>Remarks</Form.Label>
          <Form.Control
            type="text"
            className="mb-3"
            value={salesInvoice.remarks || ''}
            onChange={e => handleSalesFormChange('remarks', e.target.value)}
            onBlur={saveEditedInvoice}
          />
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col} controlId="membershiNumber">
          <Form.Label>Membership Number</Form.Label>
          <Form.Control
            type="text"
            className="mb-3"
            value={memberShipNumberInput}
            onChange={e =>
              setMemberShipNumberInput(e.target.value.toUpperCase())
            }
          />
        </Form.Group>
        <Form.Group as={Col} controlId="discountCode">
          <Form.Label>Discount Code</Form.Label>
          <div className="mb-3 d-flex align-items-center">
            <span className="me-2">{salesInvoice.discountCode || 'No discount code applied'}</span>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => setShowDiscountModal(true)}
            >
              Edit
            </Button>
          </div>
        </Form.Group>
      </Row>
      <Row>
        <Form.Group className="d-flex flex-column">
          <Form.Label className="mb-3">Payment Options</Form.Label>
          <div className="flex-row" onBlur={saveEditedInvoice}>
            {paymentMethods &&
              paymentMethods.map(method => (
                <Form.Check
                  inline
                  key={method.paymentMethodId}
                  type="radio"
                  label={method.name}
                  name="inline-radio"
                  className="mb-2"
                  value={method.paymentMethodId}
                  checked={
                    salesInvoice.paymentMethodId === method.paymentMethodId
                  }
                  onChange={e =>
                    handleSalesFormChange(
                      'paymentMethodId',
                      Number(e.target.value)
                    )
                  }
                />
              ))}
          </div>
        </Form.Group>
        {isSalesInvoiceCreated && (
          <div>
            <Form.Label>Unique Code</Form.Label>
            <h5 className="ms-3">{uniqueCode}</h5>
          </div>
        )}
      </Row>

      <Modal show={showDiscountModal} onHide={() => setShowDiscountModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Discount Code</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Discount Code</Form.Label>
            <Form.Control
              type="text"
              value={discountCodeInput}
              onChange={(e) => setDiscountCodeInput(e.target.value)}
              placeholder="Enter discount code"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDiscountModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmitDiscountCode}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateSalesInvoiceInputs;
